import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    recipeId: String 
  }
  
  static targets = [
    "modal", 
    "amount", 
    "fraction", 
    "measurement", 
    "nameEditor", 
    "preview",
    "saveBtn",
    "ingredientsList",
    "instructionModal",
    "stepEditor",
    "instructionSaveBtn",
    "instructionsList"
  ]

  connect() {
    this.storeOriginalData()
    this.setupDragAndDrop()
    this.setupFormSubmission()
    
    // Only set up event listeners if targets exist
    if (this.hasAmountTarget) {
      this.amountTarget.addEventListener('input', () => this.updatePreview())
    }
    if (this.hasFractionTarget) {
      this.fractionTarget.addEventListener('change', () => this.updatePreview())
    }
    if (this.hasMeasurementTarget) {
      this.measurementTarget.addEventListener('input', () => this.updatePreview())
    }
    if (this.hasNameEditorTarget) {
      this.nameEditorTarget.addEventListener('input', () => this.updatePreview())
      this.nameEditorTarget.addEventListener('keyup', () => this.updatePreview())
    }
    
    // Initial preview if we have the preview target
    if (this.hasPreviewTarget) {
      this.updatePreview()
    }
  }

  showModal() {
    this.modalTarget.style.display = 'flex'
    this.resetForm()
    this.updatePreview()
  }

  hideModal() {
    this.modalTarget.style.display = 'none'
  }

  closeModal(event) {
    // Close if clicking outside modal content
    if (event.target === this.modalTarget) {
      this.hideModal()
    }
  }

  resetForm() {
    this.amountTarget.value = ''
    this.fractionTarget.value = ''
    this.measurementTarget.value = ''
    this.nameEditorTarget.innerHTML = ''
    this.previewTarget.innerHTML = ''
  }

  updatePreview() {
    const amount = this.amountTarget.value.trim()
    const fraction = this.fractionTarget.value
    const measurement = this.measurementTarget.value.trim()
    const name = this.nameEditorTarget.value ? this.nameEditorTarget.value.trim() : this.nameEditorTarget.innerText.trim()
    
    let preview = ''
    
    // Build the preview string
    if (amount && amount !== '0') {
      preview += amount
      if (fraction) {
        preview += ' ' + fraction
      }
      if (measurement) {
        preview += ' ' + measurement
      }
    } else if (fraction) {
      preview += fraction
      if (measurement) {
        preview += ' ' + measurement
      }
    } else if (measurement) {
      preview += measurement
    }
    
    if (name) {
      if (preview) {
        preview += ' ' + name
      } else {
        preview = name
      }
    }
    
    this.previewTarget.innerHTML = preview || '<em class="text-muted">Enter ingredient details to see preview...</em>'
  }

  saveIngredient() {
    const amount = this.amountTarget.value.trim()
    const fraction = this.fractionTarget.value
    const measurement = this.measurementTarget.value.trim()
    const name = this.nameEditorTarget.innerHTML.trim()
    
    // Validate required fields
    if (!name) {
      alert('Please enter an ingredient name.')
      return
    }
    
    // Create ingredient object
    const ingredient = {
      type: 'ingredient',
      amount: amount || null,
      fraction: fraction || null,
      measurement: measurement || null,
      name: name
    }
    
    // Add ingredient to the list visually
    this.addIngredientToList(ingredient)
    
    // Hide modal
    this.hideModal()
  }

  addIngredientToList(ingredient) {
    const existingEmptyMessage = this.ingredientsListTarget.querySelector('.text-muted')
    
    // Remove "No ingredients" message if it exists
    if (existingEmptyMessage && existingEmptyMessage.textContent.includes('No ingredients')) {
      existingEmptyMessage.remove()
    }
    
    // Create ingredient item HTML
    const ingredientItem = document.createElement('div')
    ingredientItem.className = 'd-flex align-items-center mb-2 ingredient-item'
    ingredientItem.draggable = true
    
    // Store the ingredient data on the element for easy retrieval
    ingredientItem.dataset.ingredientData = JSON.stringify(ingredient)
    
    // Build display text with proper formatting
    let displayText = ''
    
    // Build quantity part (amount + fraction)
    if (ingredient.amount) {
      displayText += ingredient.amount
      if (ingredient.fraction) {
        displayText += ' ' + ingredient.fraction
      }
    } else if (ingredient.fraction) {
      displayText += ingredient.fraction
    }
    
    // Add measurement
    if (ingredient.measurement) {
      if (displayText) {
        displayText += ' ' + ingredient.measurement
      } else {
        displayText += ingredient.measurement
      }
    }
    
    // Add name with proper spacing
    if (ingredient.name) {
      // For name, if it contains HTML, render it properly, otherwise just use text
      const nameDiv = document.createElement('div')
      nameDiv.innerHTML = ingredient.name
      const nameText = nameDiv.innerText || nameDiv.textContent || ''
      
      if (displayText && nameText) {
        displayText += ' ' + nameText
      } else if (nameText) {
        displayText = nameText
      }
    }
    
    ingredientItem.innerHTML = `
      <div class="drag-handle me-3" style="cursor: grab; padding: 8px;">
        <img src="/assets/icons/list-item-grip.svg" alt="Drag handle" class="text-muted" style="width: 16px; height: 16px;" />
      </div>
      <div class="flex-grow-1 py-2">${displayText}</div>
      <div class="ms-2">
        <button type="button" class="me-1" style="padding: 8px; cursor: pointer; background: none; border: none;">
          <img src="/assets/icons/list-item-edit.svg" alt="Edit" style="width: 16px; height: 16px;" />
        </button>
        <button type="button" style="padding: 8px; cursor: pointer; background: none; border: none;">
          <img src="/assets/icons/list-item-delete.svg" alt="Delete" style="width: 16px; height: 16px;" />
        </button>
      </div>
    `
    
    // Add to list
    this.ingredientsListTarget.appendChild(ingredientItem)
    
    // Setup drag and drop for the new item
    this.setupItemDragAndDrop(ingredientItem, 'ingredient')
  }

  // Instruction Modal Methods
  showInstructionModal() {
    this.instructionModalTarget.style.display = 'flex'
    this.resetInstructionForm()
  }

  hideInstructionModal() {
    this.instructionModalTarget.style.display = 'none'
  }

  closeInstructionModal(event) {
    // Close if clicking outside modal content
    if (event.target === this.instructionModalTarget) {
      this.hideInstructionModal()
    }
  }

  resetInstructionForm() {
    this.stepEditorTarget.innerHTML = ''
  }

  saveInstruction() {
    const step = this.stepEditorTarget.innerHTML.trim()
    
    // Validate required fields
    if (!step) {
      alert('Please enter an instruction step.')
      return
    }
    
    // Create instruction object
    const instruction = {
      type: 'step',
      step: step
    }
    
    // Add instruction to the list visually
    this.addInstructionToList(instruction)
    
    // Hide modal
    this.hideInstructionModal()
  }

  addInstructionToList(instruction) {
    const existingEmptyMessage = this.instructionsListTarget.querySelector('.text-muted')
    
    // Remove "No instructions" message if it exists
    if (existingEmptyMessage && existingEmptyMessage.textContent.includes('No instructions')) {
      existingEmptyMessage.remove()
    }
    
    // Create instruction item HTML
    const instructionItem = document.createElement('div')
    instructionItem.className = 'd-flex align-items-start mb-3 instruction-item'
    instructionItem.draggable = true
    
    // Store the instruction data on the element for easy retrieval
    instructionItem.dataset.instructionData = JSON.stringify(instruction)
    
    instructionItem.innerHTML = `
      <div class="drag-handle me-3 mt-2" style="cursor: grab; padding: 8px;">
        <img src="/assets/icons/list-item-grip.svg" alt="Drag handle" class="text-muted" style="width: 16px; height: 16px;" />
      </div>
      <div class="flex-grow-1 py-2">
        <div class="fw-semibold mb-1">Step</div>
        <div class="text-muted">${instruction.step}</div>
      </div>
      <div class="ms-2">
        <button type="button" class="me-1" style="padding: 8px; cursor: pointer; background: none; border: none;">
          <img src="/assets/icons/list-item-edit.svg" alt="Edit" style="width: 16px; height: 16px;" />
        </button>
        <button type="button" style="padding: 8px; cursor: pointer; background: none; border: none;">
          <img src="/assets/icons/list-item-delete.svg" alt="Delete" style="width: 16px; height: 16px;" />
        </button>
      </div>
    `
    
    // Add to list
    this.instructionsListTarget.appendChild(instructionItem)
    
    // Setup drag and drop for the new item
    this.setupItemDragAndDrop(instructionItem, 'instruction')
  }

  // Drag and Drop functionality
  setupDragAndDrop() {
    // Setup existing ingredients
    if (this.hasIngredientsListTarget) {
      const ingredientItems = this.ingredientsListTarget.querySelectorAll('.ingredient-item')
      ingredientItems.forEach(item => {
        this.setupItemDragAndDrop(item, 'ingredient')
      })
    }

    // Setup existing instructions
    if (this.hasInstructionsListTarget) {
      const instructionItems = this.instructionsListTarget.querySelectorAll('.instruction-item')
      instructionItems.forEach(item => {
        this.setupItemDragAndDrop(item, 'instruction')
      })
    }
  }

  setupItemDragAndDrop(item, type) {
    item.addEventListener('dragstart', (e) => this.handleDragStart(e, type))
    item.addEventListener('dragover', (e) => this.handleDragOver(e))
    item.addEventListener('drop', (e) => this.handleDrop(e, type))
    item.addEventListener('dragend', (e) => this.handleDragEnd(e))
  }

  handleDragStart(e, type) {
    this.draggedElement = e.target
    this.draggedType = type
    e.target.style.opacity = '0.5'
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', e.target.outerHTML)
  }

  handleDragOver(e) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    
    const container = e.target.closest('.ingredient-item, .instruction-item')?.parentNode
    if (!container || !this.draggedElement) return
    
    const afterElement = this.getDragAfterElement(container, e.clientY)
    
    if (afterElement == null) {
      container.appendChild(this.draggedElement)
    } else {
      container.insertBefore(this.draggedElement, afterElement)
    }
  }

  handleDrop(e, type) {
    e.preventDefault()
    if (this.draggedType !== type) {
      return // Don't allow dropping ingredients in instructions or vice versa
    }
    
    // Ensure data attributes are preserved after drag operation
    this.restoreDataAfterDrag(type)
    
    // Just update the visual order - actual saving happens when user clicks Save
    console.log(`${type} order updated visually`)
  }

  handleDragEnd(e) {
    e.target.style.opacity = '1'
    this.draggedElement = null
    this.draggedType = null
  }

  getDragAfterElement(container, y) {
    const draggableElements = [...container.querySelectorAll('.ingredient-item:not(.dragging), .instruction-item:not(.dragging)')]
    
    return draggableElements.reduce((closest, child) => {
      const box = child.getBoundingClientRect()
      const offset = y - box.top - box.height / 2
      
      if (offset < 0 && offset > closest.offset) {
        return { offset: offset, element: child }
      } else {
        return closest
      }
    }, { offset: Number.NEGATIVE_INFINITY }).element
  }

  restoreDataAfterDrag(type) {
    // Re-run storeOriginalData to ensure any items missing data get it restored
    if (type === 'instruction') {
      const instructionItems = this.instructionsListTarget.querySelectorAll('.instruction-item')
      instructionItems.forEach(item => {
        if (!item.dataset.instructionData) {
          const instruction = this.parseExistingInstruction(item)
          if (instruction) {
            item.dataset.instructionData = JSON.stringify(instruction)
          }
        }
      })
    } else if (type === 'ingredient') {
      const ingredientItems = this.ingredientsListTarget.querySelectorAll('.ingredient-item')
      ingredientItems.forEach(item => {
        if (!item.dataset.ingredientData) {
          const ingredient = this.parseExistingIngredient(item)
          if (ingredient) {
            item.dataset.ingredientData = JSON.stringify(ingredient)
          }
        }
      })
    }
  }

  // Store original data from existing items
  storeOriginalData() {
    // Store data on existing ingredient items
    if (this.hasIngredientsListTarget) {
      const ingredientItems = this.ingredientsListTarget.querySelectorAll('.ingredient-item')
      ingredientItems.forEach(item => {
        if (!item.dataset.ingredientData) {
          const ingredient = this.parseExistingIngredient(item)
          if (ingredient) {
            item.dataset.ingredientData = JSON.stringify(ingredient)
          }
        }
      })
    }

    // Store data on existing instruction items
    if (this.hasInstructionsListTarget) {
      const instructionItems = this.instructionsListTarget.querySelectorAll('.instruction-item')
      instructionItems.forEach(item => {
        if (!item.dataset.instructionData) {
          const instruction = this.parseExistingInstruction(item)
          if (instruction) {
            item.dataset.instructionData = JSON.stringify(instruction)
          }
        }
      })
    }
  }

  // Form submission handling
  setupFormSubmission() {
    // Find the main form and add a submit handler
    const form = document.querySelector('.recipe-details-form')
    if (form) {
      form.addEventListener('submit', (e) => this.handleFormSubmit(e))
    }
  }

  handleFormSubmit(e) {
    // Always collect current state to preserve reordering
    const ingredients = this.collectIngredientsFromDOM()
    const instructions = this.collectInstructionsFromDOM()
    
    // Create hidden inputs to send the data
    this.addHiddenInput(e.target, 'recipe[ingredients]', JSON.stringify(ingredients))
    this.addHiddenInput(e.target, 'recipe[instructions]', JSON.stringify(instructions))
  }

  addHiddenInput(form, name, value) {
    // Remove existing hidden input if it exists
    const existingInput = form.querySelector(`input[name="${name}"]`)
    if (existingInput) {
      existingInput.remove()
    }
    
    // Add new hidden input
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  }

  collectIngredientsFromDOM() {
    const ingredients = []
    const ingredientItems = this.ingredientsListTarget.querySelectorAll('.ingredient-item')
    
    ingredientItems.forEach(item => {
      if (item.dataset.ingredientData) {
        const ingredient = JSON.parse(item.dataset.ingredientData)
        ingredients.push(ingredient)
      }
    })
    
    return ingredients
  }

  collectInstructionsFromDOM() {
    const instructions = []
    const instructionItems = this.instructionsListTarget.querySelectorAll('.instruction-item')
    
    instructionItems.forEach(item => {
      if (item.dataset.instructionData) {
        const instruction = JSON.parse(item.dataset.instructionData)
        instructions.push(instruction)
      }
    })
    
    return instructions
  }

  parseExistingIngredient(item) {
    // Parse existing ingredients from the DOM structure
    const contentDiv = item.querySelector('.flex-grow-1')
    if (!contentDiv) return null
    
    // Check if this is a header
    const strongTag = contentDiv.querySelector('strong')
    if (strongTag) {
      return {
        type: 'header',
        text: strongTag.textContent.trim()
      }
    }
    
    // For regular ingredients, try to extract the parts from the HTML
    const rawHTML = contentDiv.innerHTML.trim()
    
    // This is a simplified parser - for existing ingredients we'll preserve them as-is
    // The original data structure will be maintained
    return {
      type: 'ingredient',
      amount: null,
      fraction: null, 
      measurement: null,
      name: rawHTML // Preserve the HTML content including any rich text
    }
  }

  parseExistingInstruction(item) {
    // Check if this is a header (by looking for strong tag in the main content)
    const strongTag = item.querySelector('.flex-grow-1 strong')
    if (strongTag) {
      return {
        type: 'header',
        text: strongTag.textContent.trim()
      }
    }
    
    // Parse existing instructions from the DOM structure
    // Use a more specific selector to get the actual text div inside flex-grow-1
    const textDiv = item.querySelector('.flex-grow-1 .text-muted')
    
    if (!textDiv) {
      return null
    }
    
    // Use textContent instead of innerHTML to avoid HTML tags
    const stepContent = textDiv.textContent.trim()
    
    // Regular step instruction
    if (stepContent) {
      return {
        type: 'step',
        step: stepContent
      }
    }
    
    return null
  }
}